import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Wallet, Ban, CheckCircle2, ShieldCheck, ShieldOff, Clapperboard, Film, UserCog, ChevronDown, ChevronRight, Trash2, Undo2, AlertTriangle, History, Clock, CreditCard, Zap, Youtube } from "lucide-react";
import api, { apiErr, fmtCredits } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import AdminUserActivity from "../components/AdminUserActivity";

const eur = (n) => `${(n ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return null; }
};
const fmtDateTime = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString("es-ES", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return null; }
};
const KIND_LABELS = {
  script: "Guion", render: "Render", calendar: "Calendario", analyze: "Análisis",
  recommendations: "Recomendaciones", thumbnail: "Miniatura", narration_premium: "Narración premium",
  broll_ia_image: "B-roll con IA", other: "Otro",
};
const RENDER_STATUS_COLOR = {
  completo: "text-emerald-600", rendering: "text-amber-600", error: "text-rose-600",
};

// Chip de saldo compacto: cada origen (plan, comprado, hitos, referidos...) por separado en vez
// de agrupados dentro de recuadros grandes con el desglose escondido en un title.
function Stat({ label, value, tone = "slate" }) {
  const toneClasses = tone === "emerald"
    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
    : "border-slate-200 bg-white text-slate-900";
  return (
    <div className={`rounded-md border px-2.5 py-1.5 ${toneClasses}`}>
      <div className="text-sm font-semibold leading-tight">{value}</div>
      <div className={`text-[11px] leading-tight ${tone === "emerald" ? "text-emerald-700" : "text-slate-400"}`}>{label}</div>
    </div>
  );
}

/**
 * Página propia de gestión de un usuario (antes era un modal, AdminUserDialog.jsx) — el mismo
 * contenido (saldo, plan, suspender, rol, "ver como este usuario", canales/vídeos) más el
 * desglose de coste/ingreso/beneficio POR VÍDEO (no solo el agregado por tipo de acción de
 * todo el usuario), expandible por concepto (guion, narración, render, miniatura...).
 */
export default function AdminUserPage() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [bucket, setBucket] = useState("topup");
  const [credits, setCredits] = useState("");
  const [balanceReason, setBalanceReason] = useState("");
  const balanceRequest = useRef(null);
  const planRequest = useRef(null);
  const [plan, setPlan] = useState("");
  const [busy, setBusy] = useState(false);
  const [openChannel, setOpenChannel] = useState(null);
  const [openVideo, setOpenVideo] = useState(null);
  const [impersonating, setImpersonating] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [profitOpen, setProfitOpen] = useState(false);

  const load = useCallback(() => {
    if (!uid) return;
    setData(null);
    api.get(`/admin/users/${uid}`).then(({ data }) => {
      setData(data); setPlan(data.user.plan);
    }).catch((e) => { toast.error(apiErr(e)); navigate("/admin?tab=users"); });
  }, [uid, navigate]);

  useEffect(() => { load(); }, [load]);

  const u = data?.user;
  const profitTotals = (data?.by_kind || []).reduce((acc, k) => ({
    revenue: acc.revenue + (k.revenue || 0), cost: acc.cost + (k.cost || 0), profit: acc.profit + (k.profit || 0),
  }), { revenue: 0, cost: 0, profit: 0 });

  const adjustBalance = async () => {
    const val = parseFloat(credits);
    if (!val) return;
    setBusy(true);
    try {
      const signature = JSON.stringify([uid, bucket, val, balanceReason]);
      if (balanceRequest.current?.signature !== signature) {
        balanceRequest.current = { signature, id: crypto.randomUUID() };
      }
      await api.post(`/admin/users/${uid}/balance`, { bucket, credits: val, reason: balanceReason, operation_id: balanceRequest.current.id });
      balanceRequest.current = null;
      toast.success("Saldo actualizado.");
      setCredits(""); setBalanceReason(""); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const changePlan = async () => {
    if (!plan || plan === u.plan) return;
    setBusy(true);
    try {
      const signature = JSON.stringify([uid, plan]);
      if (planRequest.current?.signature !== signature) {
        planRequest.current = { signature, id: crypto.randomUUID() };
      }
      await api.post(`/admin/users/${uid}/plan`, { plan, operation_id: planRequest.current.id });
      planRequest.current = null;
      toast.success("Plan actualizado.");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const toggleSuspend = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/users/${uid}/suspend`);
      toast.success(r.suspended ? "Usuario suspendido." : "Usuario reactivado.");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const toggleRole = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/users/${uid}/role`);
      toast.success(r.role === "admin" ? "Ahora es administrador." : "Rol de administrador retirado.");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  // Borrado REVERSIBLE (ver admin_delete_user en el backend): el usuario deja de poder entrar y
  // desaparece del listado, pero sus datos y archivos siguen ahí. La confirmación pide escribir
  // el email a propósito — un window.confirm de "¿seguro?" se acepta sin leer.
  const toggleDeleted = async () => {
    if (!u.deleted) {
      const typed = window.prompt(
        `Vas a borrar la cuenta de ${u.email}.\n\n` +
        "No podrá entrar y se cancelará su suscripción al final del periodo ya pagado. " +
        "Sus datos y archivos NO se borran, así que podrás restaurarla.\n\n" +
        "Escribe su email para confirmar:");
      if (typed === null) return;
      if (typed.trim().toLowerCase() !== (u.email || "").toLowerCase()) {
        toast.error("El email no coincide. No se ha borrado nada.");
        return;
      }
    }
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/users/${uid}/delete`);
      if (r.deleted) {
        toast.success(r.had_subscription && !r.subscription_cancelled
          ? "Usuario borrado, pero NO se pudo cancelar su suscripción en Stripe — revísalo a mano."
          : "Usuario borrado.");
      } else {
        toast.success("Usuario restaurado.");
      }
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  // Borrado DEFINITIVO (ver admin_purge_user en el backend): solo posible con la cuenta ya
  // borrada (borrado blando primero, como freno). Irreversible de verdad — arrastra proyectos,
  // canales, archivos en Backblaze... Confirmación reforzada: hay que escribir "BORRAR" a
  // propósito, más estricto que el email del borrado blando porque aquí no hay vuelta atrás.
  const purgeUser = async () => {
    const typed = window.prompt(
      `Vas a borrar DEFINITIVAMENTE la cuenta de ${u.email}.\n\n` +
      "Esto borra también sus proyectos, canales y archivos. No hay forma de deshacerlo.\n\n" +
      'Escribe BORRAR (en mayúsculas) para confirmar:');
    if (typed === null) return;
    if (typed.trim() !== "BORRAR") {
      toast.error("No escribiste BORRAR. No se ha borrado nada.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/users/${uid}/purge`);
      toast.success("Usuario borrado definitivamente.");
      navigate("/admin?tab=users");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  // "Ver como este usuario": sustituye la sesión activa por la suya (backend guarda quién es
  // el admin real en otra cookie para poder volver, ver /auth/impersonate/stop). Se abre en una
  // pestaña NUEVA en vez de navegar la actual: la cookie access_token es del navegador entero
  // (no por pestaña), así que si reusáramos esta misma pestaña el panel de admin quedaría
  // suplantado también. Dejando esta pestaña quieta, AuthContext no vuelve a leer /auth/me aquí
  // (solo lo hace al montar) y la sesión de admin sigue intacta mientras la pestaña nueva
  // arranca de cero con la cookie ya cambiada.
  const impersonate = async () => {
    if (!window.confirm(`¿Entrar en la cuenta de ${u.email} como si fueras él/ella? Se abrirá en una pestaña nueva; esta la puedes dejar abierta.`)) return;
    setImpersonating(true);
    try {
      await api.post(`/admin/users/${uid}/impersonate`);
      window.open("/app", "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(apiErr(e)); } finally { setImpersonating(false); }
  };

  if (user && user.role !== "admin") return <div className="grid min-h-screen place-items-center text-slate-500">Acceso restringido.</div>;

  if (!data) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-user-page">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button onClick={() => navigate("/admin?tab=users")} data-testid="admin-user-back-btn"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100">
              <ArrowLeft size={18} />
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-bold text-slate-900">{u.name || "Usuario"}
                {u.role === "admin" && <span className="ml-2 align-middle rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">Admin</span>}
              </h1>
              <p className="truncate text-sm text-slate-500">{u.email}</p>
            </div>
          </div>
          <button onClick={impersonate} disabled={impersonating} data-testid="admin-impersonate-btn"
            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-60">
            {impersonating ? <Loader2 size={15} className="animate-spin" /> : <UserCog size={15} />} Ver como este usuario
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl space-y-5 px-4 py-6 sm:px-6">
        {u.payment_overdue && (
          <div className="flex gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-800" data-testid="admin-payment-overdue">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Impago: pago de suscripción pendiente{u.payment_failure?.amount_label ? ` (${u.payment_failure.amount_label})` : ""}</p>
              <p>Los créditos de esta renovación no se abonan hasta confirmar el pago.</p>
              {u.payment_failure?.email_sent_at && <p className="mt-1 text-xs">Aviso por correo enviado: {fmtDateTime(u.payment_failure.email_sent_at)}</p>}
            </div>
          </div>
        )}
        {/* La bolsa permanente se muestra separada por ORIGEN, cada uno su propio chip — los
            premios (hitos, referidos, bienvenida, compensación de soporte) se abonan a la misma
            bolsa que los comprados porque necesitan no caducar en la renovación igual que ellos,
            pero agruparlos todos bajo "comprados" era mentir: el usuario no los pagó. */}
        <div className="flex flex-wrap gap-2">
          <Stat label="Plan actual" value={u.plan_label} />
          <Stat label="créditos plan" value={fmtCredits(u.plan_seconds)} />
          <Stat label="créditos comprados" value={fmtCredits(u.creditos_comprados ?? u.topup_seconds)} />
          {u.creditos_ganados?.hitos > 0 && <Stat tone="emerald" label="hitos" value={fmtCredits(u.creditos_ganados.hitos)} />}
          {u.creditos_ganados?.referidos > 0 && <Stat tone="emerald" label="referidos" value={fmtCredits(u.creditos_ganados.referidos)} />}
          {u.creditos_ganados?.bienvenida > 0 && <Stat tone="emerald" label="bienvenida" value={fmtCredits(u.creditos_ganados.bienvenida)} />}
          {u.creditos_ganados?.compensacion > 0 && <Stat tone="emerald" label="compensación" value={fmtCredits(u.creditos_ganados.compensacion)} />}
        </div>

        {/* pending_plan_change ya lo devolvía el backend (bajada/cancelación programada al final
            del ciclo ya pagado) pero nunca se pintaba aquí — un admin no tenía forma de saber que
            un usuario había pulsado cancelar hasta que, semanas después, el plan cambiaba solo. */}
        {u.pending_plan_change && (
          <div className="flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800" data-testid="admin-pending-plan-change">
            <Ban size={15} className="shrink-0" />
            {u.pending_plan_change.plan === "gratis"
              ? `Canceló su suscripción — pasará a plan gratis el ${fmtDate(u.pending_plan_change.effective_at)}.`
              : `Cambio de plan programado a "${u.pending_plan_change.plan}" el ${fmtDate(u.pending_plan_change.effective_at)}.`}
          </div>
        )}

        {/* Historial de cuenta — colapsable: el resumen (fecha de alta, renovación, nº de pagos)
            va siempre visible aunque esté cerrada, para no obligar a expandir solo para ver lo
            básico; el detalle completo (cada pago, cada vez que se apuntó a lista de espera)
            queda oculto salvo que haga falta investigar algo concreto. */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <button onClick={() => setHistoryOpen((v) => !v)} data-testid="admin-history-toggle"
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50">
            <span className="flex min-w-0 items-center gap-2">
              {historyOpen ? <ChevronDown size={15} className="shrink-0 text-slate-400" /> : <ChevronRight size={15} className="shrink-0 text-slate-400" />}
              <History size={16} className="shrink-0 text-slate-500" />
              <span className="text-sm font-semibold text-slate-700 shrink-0">Historial de cuenta</span>
              <span className="min-w-0 truncate text-xs text-slate-400">
                {[
                  fmtDate(u.created_at) && `Alta: ${fmtDate(u.created_at)}`,
                  u.payment_overdue ? "Pago pendiente" : u.plan !== "gratis" && fmtDate(u.plan_renews_at) && `Renovación: ${fmtDate(u.plan_renews_at)}`,
                  data.payments.length > 0 && `${data.payments.length} pago${data.payments.length === 1 ? "" : "s"}`,
                  data.payment_failures?.length > 0 && `${data.payment_failures.length} impago${data.payment_failures.length === 1 ? "" : "s"}`,
                  data.cancellations?.length > 0 && `${data.cancellations.length} solicitud${data.cancellations.length === 1 ? "" : "es"} de baja`,
                  data.waitlist.length > 0 && `${data.waitlist.length} en lista de espera`,
                ].filter(Boolean).join(" · ") || "Sin actividad registrada"}
              </span>
            </span>
          </button>
          {historyOpen && (
            <div className="border-t border-slate-100 px-4 py-3 space-y-4">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <span className="text-slate-500">Fecha de alta: <b className="text-slate-800">{fmtDate(u.created_at) || "—"}</b></span>
                {u.plan !== "gratis" && !u.payment_overdue && (
                  <span className="text-slate-500">Próxima renovación: <b className="text-slate-800">{fmtDate(u.plan_renews_at) || "—"}</b></span>
                )}
              </div>

              {u.acquisition && <div className="text-sm text-slate-600">
                <p className="font-semibold text-slate-700">Procedencia del registro</p>
                <p>{u.acquisition.source === "direct_or_unknown" ? "Directa / desconocida" : u.acquisition.source}
                  {u.acquisition.utm_campaign ? ` · ${u.acquisition.utm_campaign}` : ""}
                  {u.acquisition.ad_name || u.acquisition.utm_content ? ` · ${u.acquisition.ad_name || u.acquisition.utm_content}` : ""}</p>
                {u.acquisition.ad_id && <p className="text-xs text-slate-400">Anuncio: {u.acquisition.ad_id}</p>}
              </div>}

              <div>
                <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <Clock size={13} /> Lista de espera
                </div>
                {data.waitlist.length === 0 ? (
                  <p className="text-sm text-slate-400">Nunca se apuntó a ninguna.</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {data.waitlist.map((w, i) => (
                      <li key={i} className="text-slate-600">Plan <b className="text-slate-800">{w.plan}</b> · {fmtDate(w.created_at)}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <CreditCard size={13} /> Pagos realizados
                </div>
                {data.payments.length === 0 ? (
                  <p className="text-sm text-slate-400">Sin pagos todavía.</p>
                ) : (
                  <ul className="space-y-1.5 text-sm">
                    {data.payments.map((p, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600">
                        {p.kind === "topup" ? <Zap size={14} className="shrink-0 text-amber-500" /> : <CreditCard size={14} className="shrink-0 text-blue-500" />}
                        <span className="flex-1 min-w-0">
                          {p.kind === "topup" ? `Recarga (${p.pack || "pack"})` : `Plan ${p.plan || "—"} (${p.period === "annual" ? "anual" : "mensual"})`}
                        </span>
                        <span className="font-semibold text-slate-800">{eur(p.amount)}</span>
                        <span className="shrink-0 text-xs text-slate-400">{fmtDate(p.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {data.cancellations?.length > 0 && (
                <div data-testid="admin-cancellation-history">
                  <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                    <Ban size={13} /> Solicitudes de baja
                  </div>
                  <ul className="space-y-3 text-sm">
                    {data.cancellations.map((cancellation, i) => (
                      <li key={`${cancellation.creado}-${i}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2 text-slate-600">
                          <span>Plan <b className="text-slate-800">{cancellation.plan || "—"}</b></span>
                          <span className="text-xs text-slate-500">{fmtDateTime(cancellation.creado)}</span>
                        </div>
                        <p className="mt-1 font-semibold text-slate-800">{cancellation.motivo_texto}</p>
                        <p className="whitespace-pre-wrap break-words text-slate-600">{cancellation.comentario || "Sin comentario adicional."}</p>
                        {cancellation.videos_completados != null && <p className="mt-1 text-xs text-slate-500">Vídeos completados al solicitarla: {cancellation.videos_completados}</p>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.payment_failures?.length > 0 && (
                <div data-testid="admin-payment-failure-history">
                  <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                    <AlertTriangle size={13} /> Impagos
                  </div>
                  <ul className="space-y-3 text-sm">
                    {data.payment_failures.map((failure) => (
                      <li key={failure.invoice_id}>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="flex-1 text-slate-700">Plan {failure.plan || "—"} ({failure.period === "annual" ? "anual" : "mensual"})</span>
                          <span className="font-semibold text-slate-800">{failure.amount_label}</span>
                          <span className={failure.status === "paid" ? "text-xs text-emerald-700" : "text-xs font-semibold text-rose-700"}>
                            {failure.status === "paid" ? "Regularizado" : "Pendiente de pago"}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          {failure.failed_at ? "Cobro fallido" : "Impago detectado"}: {fmtDateTime(failure.failed_at || failure.detected_at)}
                        </p>
                        {failure.email_sent_at && <p className="text-xs text-slate-500">Aviso por correo enviado: {fmtDateTime(failure.email_sent_at)}</p>}
                        {failure.paid_at && <p className="text-xs text-emerald-700">Pago confirmado: {fmtDateTime(failure.paid_at)}</p>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <AdminUserActivity key={uid} uid={uid} currentBalance={(u.plan_seconds || 0) + (u.topup_seconds || 0)} />

        {/* Rentabilidad por tipo (agregado de todos los vídeos del usuario) — colapsable, mismo
            criterio que "Historial de cuenta": el resumen agregado va siempre visible, la tabla
            desglosada por concepto solo si hace falta mirar de dónde sale el número. */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <button onClick={() => setProfitOpen((v) => !v)} data-testid="admin-profit-toggle"
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50">
            <span className="flex min-w-0 items-center gap-2">
              {profitOpen ? <ChevronDown size={15} className="shrink-0 text-slate-400" /> : <ChevronRight size={15} className="shrink-0 text-slate-400" />}
              <span className="text-sm font-semibold text-slate-700 shrink-0">Rentabilidad por acción (todos los vídeos)</span>
              <span className="min-w-0 truncate text-xs text-slate-400">
                {data.by_kind.length === 0 ? "Sin actividad" : (
                  <>
                    <span className="text-emerald-600">{eur(profitTotals.revenue)}</span> ·{" "}
                    <span className="text-rose-600">−{eur(profitTotals.cost)}</span> ·{" "}
                    <span className="font-semibold text-blue-600">= {eur(profitTotals.profit)}</span> · {data.by_kind.length} tipo{data.by_kind.length === 1 ? "" : "s"} de acción
                  </>
                )}
              </span>
            </span>
          </button>
          {profitOpen && (
            <table className="w-full text-sm border-t border-slate-100">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr><th className="px-3 py-2">Acción</th><th className="px-3 py-2 text-right">Nº</th>
                  <th className="px-3 py-2 text-right">Ingreso</th><th className="px-3 py-2 text-right">Coste</th>
                  <th className="px-3 py-2 text-right">Beneficio</th></tr>
              </thead>
              <tbody>
                {data.by_kind.length === 0 ? (
                  <tr><td colSpan={5} className="py-6 text-center text-slate-400">Sin actividad.</td></tr>
                ) : data.by_kind.map((k) => (
                  <tr key={k.kind} className="border-t border-slate-100">
                    <td className="px-3 py-2">{KIND_LABELS[k.kind] || k.kind}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{k.count}</td>
                    <td className="px-3 py-2 text-right text-emerald-600">{eur(k.revenue)}</td>
                    <td className="px-3 py-2 text-right text-rose-600">{eur(k.cost)}</td>
                    <td className="px-3 py-2 text-right font-medium text-blue-600">{eur(k.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Acciones de gestión */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><Wallet size={16} /> Ajustar saldo</h3>
          <div className="flex flex-wrap items-center gap-2">
            <select value={bucket} onChange={(e) => setBucket(e.target.value)} data-testid="admin-balance-bucket"
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <option value="topup">Recarga (permanente)</option>
              <option value="plan">Plan (caduca)</option>
            </select>
            <input type="number" value={credits} onChange={(e) => setCredits(e.target.value)}
              placeholder="± créditos" data-testid="admin-balance-credits"
              className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input type="text" value={balanceReason} onChange={(e) => setBalanceReason(e.target.value)}
              placeholder="Motivo (ej. compensación por render)" data-testid="admin-balance-reason"
              className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <button onClick={adjustBalance} disabled={busy || !credits} data-testid="admin-balance-apply"
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              Aplicar
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-400">
            Usa valores negativos para restar créditos. Un ajuste sobre "Recarga" queda contado como
            regalo, no como compra (ver "créditos de premio" arriba) — el motivo ayuda a auditarlo luego.
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><Save size={16} /> Cambiar plan (inmediato)</h3>
          <div className="flex flex-wrap items-center gap-2">
            <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="admin-plan-select"
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <option value="gratis">Gratis</option><option value="starter">Starter</option>
              <option value="creator">Creator</option><option value="pro">Pro</option>
              <option value="studio">Studio</option>
            </select>
            <button onClick={changePlan} disabled={busy || plan === u.plan} data-testid="admin-plan-apply"
              className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50">
              Aplicar plan
            </button>
            <button onClick={toggleSuspend} disabled={busy} data-testid="admin-suspend-btn"
              className={`ml-auto flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium ${
                u.suspended ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-rose-50 text-rose-700 hover:bg-rose-100"}`}>
              {u.suspended ? <><CheckCircle2 size={15} /> Reactivar</> : <><Ban size={15} /> Suspender</>}
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><ShieldCheck size={16} /> Rol de administrador</h3>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-slate-500 flex-1 min-w-[200px]">
              {u.role === "admin"
                ? "Este usuario tiene acceso completo a la Zona Admin."
                : "Concede acceso a la Zona Admin (métricas, usuarios, markup…)."}
            </p>
            <button onClick={toggleRole} disabled={busy} data-testid="admin-role-toggle-btn"
              className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium ${
                u.role === "admin" ? "bg-rose-50 text-rose-700 hover:bg-rose-100" : "bg-violet-600 text-white hover:bg-violet-700"}`}>
              {u.role === "admin" ? <><ShieldOff size={15} /> Quitar admin</> : <><ShieldCheck size={15} /> Hacer admin</>}
            </button>
          </div>
        </div>

        {/* Zona de borrado — separada del resto y en rojo a propósito: es la única acción de
            esta página que le quita el acceso al usuario por completo. */}
        <div className={`rounded-xl border p-4 ${u.deleted ? "border-amber-300 bg-amber-50" : "border-rose-200 bg-white"}`}>
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-rose-700">
            <AlertTriangle size={16} /> {u.deleted ? "Cuenta borrada" : "Borrar cuenta"}
          </h3>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-slate-600 flex-1 min-w-[240px]">
              {u.deleted
                ? <>Esta cuenta está borrada{u.deleted_at ? ` desde el ${new Date(u.deleted_at).toLocaleDateString()}` : ""} y no puede entrar. Sus datos y archivos siguen intactos, así que puedes restaurarla.</>
                : <>No podrá entrar y se cancelará su suscripción al final del periodo ya pagado. <b>Sus datos y archivos NO se borran</b>, así que es reversible.</>}
            </p>
            <button onClick={toggleDeleted} disabled={busy} data-testid="admin-delete-user-btn"
              className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium ${
                u.deleted ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-rose-600 text-white hover:bg-rose-700"}`}>
              {u.deleted ? <><Undo2 size={15} /> Restaurar cuenta</> : <><Trash2 size={15} /> Borrar cuenta</>}
            </button>
          </div>
          {u.deleted && (
            <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-amber-200 pt-3">
              <p className="text-sm text-slate-600 flex-1 min-w-[240px]">
                <b>Borrado definitivo:</b> elimina también sus proyectos, canales y archivos. No se puede deshacer.
              </p>
              <button onClick={purgeUser} disabled={busy} data-testid="admin-purge-user-btn"
                className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-black">
                <Trash2 size={15} /> Borrar definitivamente
              </button>
            </div>
          )}
        </div>

        {/* Canales y vídeos, con coste/ingreso/beneficio por vídeo */}
        <div>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><Clapperboard size={16} /> Canales ({data.channels.length})</h3>
          {data.channels.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-200 bg-white p-4 text-center text-sm text-slate-400">Sin canales todavía.</p>
          ) : (
            <div className="space-y-2">
              {data.channels.map((ch) => {
                const open = openChannel === ch.id;
                return (
                  <div key={ch.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                    <button onClick={() => setOpenChannel(open ? null : ch.id)}
                      className="flex w-full items-center justify-between gap-2 bg-slate-50 px-3 py-2.5 text-left hover:bg-slate-100">
                      <span className="flex items-center gap-2 text-sm font-medium text-slate-800">
                        {open ? <ChevronDown size={15} className="text-slate-400" /> : <ChevronRight size={15} className="text-slate-400" />}
                        {ch.name || "Canal"}
                        {!ch.active && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">INACTIVO</span>}
                      </span>
                      <span className="text-xs text-slate-400">{ch.videos.length} vídeo{ch.videos.length === 1 ? "" : "s"}</span>
                    </button>
                    {open && (
                      ch.videos.length === 0 ? (
                        <p className="p-3 text-center text-xs text-slate-400">Sin vídeos en este canal.</p>
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {ch.videos.map((v) => {
                            const vOpen = openVideo === v.id;
                            const hasCost = (v.cost || 0) > 0 || (v.revenue || 0) > 0 || (v.credits || 0) > 0;
                            return (
                              <div key={v.id}>
                                <button onClick={() => hasCost && setOpenVideo(vOpen ? null : v.id)}
                                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-sm text-left ${hasCost ? "hover:bg-slate-50" : "cursor-default"}`}>
                                  <span className="flex min-w-0 items-center gap-2 truncate text-slate-700">
                                    {hasCost && (vOpen ? <ChevronDown size={13} className="shrink-0 text-slate-400" /> : <ChevronRight size={13} className="shrink-0 text-slate-400" />)}
                                    <Film size={14} className="shrink-0 text-slate-400" /> {v.name || "Vídeo"}
                                  </span>
                                  <span className="flex shrink-0 items-center gap-3">
                                    {hasCost && (
                                      <span className="hidden sm:flex items-center gap-2 text-xs">
                                        <span className="text-emerald-600">{eur(v.revenue)}</span>
                                        <span className="text-rose-600">−{eur(v.cost)}</span>
                                        <span className="font-semibold text-blue-600">= {eur(v.profit)}</span>
                                        <span className="text-slate-400">· {fmtCredits(v.credits || 0)} créditos</span>
                                      </span>
                                    )}
                                    <span className={`text-xs font-medium ${RENDER_STATUS_COLOR[v.render_status] || "text-slate-400"}`}>
                                      {v.render_status || v.status || "—"}
                                    </span>
                                  </span>
                                </button>
                                {vOpen && (
                                  <div className="bg-slate-50 px-3 py-2">
                                    <table className="w-full text-xs">
                                      <thead className="text-left uppercase text-slate-400">
                                        <tr><th className="py-1">Concepto</th><th className="py-1 text-right">Ingreso</th>
                                          <th className="py-1 text-right">Coste</th><th className="py-1 text-right">Beneficio</th>
                                          <th className="py-1 text-right">Créditos</th></tr>
                                      </thead>
                                      <tbody>
                                        {v.by_kind.map((k) => (
                                          <tr key={k.kind} className="border-t border-slate-200">
                                            <td className="py-1">{KIND_LABELS[k.kind] || k.kind}</td>
                                            <td className="py-1 text-right text-emerald-600">{eur(k.revenue)}</td>
                                            <td className="py-1 text-right text-rose-600">{eur(k.cost)}</td>
                                            <td className="py-1 text-right font-medium text-blue-600">{eur(k.profit)}</td>
                                            <td className="py-1 text-right text-slate-500">{fmtCredits(k.credits || 0)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    {v.regenerations?.length > 0 && (
                                      <div className="mt-3 pt-3 border-t border-slate-200">
                                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                                          Original vs. regeneraciones ({v.regenerations.length}× "Regenerar todo automáticamente")
                                        </div>
                                        <table className="w-full text-xs">
                                          <thead className="text-left uppercase text-slate-400">
                                            <tr><th className="py-1">Tramo</th><th className="py-1 text-right">Ingreso</th>
                                              <th className="py-1 text-right">Coste</th><th className="py-1 text-right">Beneficio</th>
                                              <th className="py-1 text-right">Créditos</th></tr>
                                          </thead>
                                          <tbody>
                                            <tr className="border-t border-slate-200">
                                              <td className="py-1">Original</td>
                                              <td className="py-1 text-right text-emerald-600">{eur(v.original.revenue)}</td>
                                              <td className="py-1 text-right text-rose-600">{eur(v.original.cost)}</td>
                                              <td className="py-1 text-right font-medium text-blue-600">{eur(v.original.profit)}</td>
                                              <td className="py-1 text-right text-slate-500">{fmtCredits(v.original.credits || 0)}</td>
                                            </tr>
                                            {v.regenerations.map((r, i) => (
                                              <tr key={r.at} className="border-t border-slate-200">
                                                <td className="py-1">Regeneración {i + 1} <span className="text-slate-400">· {new Date(r.at).toLocaleString("es-ES")}</span></td>
                                                <td className="py-1 text-right text-emerald-600">{eur(r.revenue)}</td>
                                                <td className="py-1 text-right text-rose-600">{eur(r.cost)}</td>
                                                <td className="py-1 text-right font-medium text-blue-600">{eur(r.profit)}</td>
                                                <td className="py-1 text-right text-slate-500">{fmtCredits(r.credits || 0)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
